import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _classPrivateFieldGet from '@babel/runtime/helpers/esm/classPrivateFieldGet';
import _classPrivateFieldSet from '@babel/runtime/helpers/esm/classPrivateFieldSet';
import { invariant, ErrorConstant, bool, hasTransactionChanged, isSelectionEmpty, isString, extensionDecorator, PlainExtension, debounce } from '@remirror/core';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { createNanoEvents } from 'nanoevents';

function _createForOfIteratorHelper(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = o[Symbol.iterator](); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/**
 * The events that can trigger a positioner update.
 */

var _handler = /*#__PURE__*/new WeakMap();

var _active = /*#__PURE__*/new WeakMap();

var _parameters = /*#__PURE__*/new WeakMap();

var _ids = /*#__PURE__*/new WeakMap();

var _updated = /*#__PURE__*/new WeakMap();

var _getActive = /*#__PURE__*/new WeakMap();

var _getID = /*#__PURE__*/new WeakMap();

var _getPosition = /*#__PURE__*/new WeakMap();

/**
 * This is the positioner. It exists to report the position of things in the
 * editor. Typically you will use it to get the position of the cursor.
 *
 * But you can be more ambitious and get the position all the active nodes of a
 * certain type. Or all visible nodes of a given time.
 *
 * The positions returned have a rect which is the viewport position.
 *
 * There are also the `top`, `left`, `right`, `bottom` which represent the
 * absolute positioned rectangle of the position in questions. For a cursor
 * position `left` and `right` are probably the same.
 */
class Positioner {
  /**
   * Create a positioner.
   */
  static create(parameter) {
    return new Positioner(parameter);
  }
  /**
   * Create a positioner from an existing positioner.
   */


  static fromPositioner(positioner, base) {
    return Positioner.create(_objectSpread(_objectSpread({}, positioner.basePositioner), base));
  }

  get basePositioner() {
    return {
      getActive: _classPrivateFieldGet(this, _getActive),
      getPosition: _classPrivateFieldGet(this, _getPosition),
      hasChanged: this.hasChanged,
      events: this.events,
      getID: _classPrivateFieldGet(this, _getID)
    };
  }

  constructor(parameter) {
    var _parameter$events;

    _handler.set(this, {
      writable: true,
      value: createNanoEvents()
    });

    _active.set(this, {
      writable: true,
      value: []
    });

    _parameters.set(this, {
      writable: true,
      value: new Map()
    });

    _ids.set(this, {
      writable: true,
      value: []
    });

    _updated.set(this, {
      writable: true,
      value: false
    });

    _getActive.set(this, {
      writable: true,
      value: void 0
    });

    _getID.set(this, {
      writable: true,
      value: void 0
    });

    _getPosition.set(this, {
      writable: true,
      value: void 0
    });

    _defineProperty(this, "addListener", (event, cb) => {
      return _classPrivateFieldGet(this, _handler).on(event, cb);
    });

    _classPrivateFieldSet(this, _getActive, parameter.getActive);

    _classPrivateFieldSet(this, _getPosition, parameter.getPosition);

    _classPrivateFieldSet(this, _getID, parameter.getID);

    this.hasChanged = parameter.hasChanged;
    this.events = (_parameter$events = parameter.events) !== null && _parameter$events !== void 0 ? _parameter$events : ['state'];
  }
  /**
   * Get the active element setters.
   */


  onActiveChanged(parameter) {
    var _this = this;

    var active = _classPrivateFieldGet(this, _getActive).call(this, parameter);

    _classPrivateFieldSet(this, _active, active);

    _classPrivateFieldSet(this, _parameters, new Map());

    _classPrivateFieldSet(this, _updated, false);

    _classPrivateFieldSet(this, _ids, []);

    var elementSetters = [];

    var _iterator = _createForOfIteratorHelper(active.entries()),
        _step;

    try {
      var _loop = function _loop() {
        var _step$value = _slicedToArray(_step.value, 2),
            index = _step$value[0],
            data = _step$value[1];

        var id = _this.getID(data, index);

        _classPrivateFieldGet(_this, _ids).push(id);

        elementSetters.push({
          setElement: element => {
            return _this.addParameter(_objectSpread(_objectSpread({}, parameter), {}, {
              data,
              element
            }), index);
          },
          id
        });
      };

      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        _loop();
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    _classPrivateFieldGet(this, _handler).emit('update', elementSetters);
  }
  /**
   * Get the id for the active data. Defaults to the index of the data item.
   */


  getID(data, index) {
    var _classPrivateFieldGet2, _classPrivateFieldGet3;

    return (_classPrivateFieldGet2 = (_classPrivateFieldGet3 = _classPrivateFieldGet(this, _getID)) === null || _classPrivateFieldGet3 === void 0 ? void 0 : _classPrivateFieldGet3.call(this, data, index)) !== null && _classPrivateFieldGet2 !== void 0 ? _classPrivateFieldGet2 : index.toString();
  }
  /**
   * Create a virtual node from the provided position to use in libraries like `react-poppy`.
   */


  getVirtualNode(position) {
    return {
      getBoundingClientRect: () => position.rect
    };
  }
  /**
   * Add a listener to the positioner events.
   */


  addParameter(parameter, index) {
    if (_classPrivateFieldGet(this, _updated)) {
      return;
    }

    _classPrivateFieldGet(this, _parameters).set(index, parameter);

    if (_classPrivateFieldGet(this, _parameters).size < _classPrivateFieldGet(this, _active).length) {
      return;
    }

    var doneParameter = [];

    var _iterator2 = _createForOfIteratorHelper(_classPrivateFieldGet(this, _active).keys()),
        _step2;

    try {
      for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
        var _index = _step2.value;

        var item = _classPrivateFieldGet(this, _parameters).get(_index);

        !item ? process.env.NODE_ENV !== "production" ? invariant(false, {
          code: ErrorConstant.INTERNAL,
          message: 'Something went wrong when retrieving the parameters'
        }) : invariant(false) : void 0;
        doneParameter.push({
          position: _classPrivateFieldGet(this, _getPosition).call(this, item),
          element: item.element,
          id: _classPrivateFieldGet(this, _ids)[_index]
        });
      }
    } catch (err) {
      _iterator2.e(err);
    } finally {
      _iterator2.f();
    }

    _classPrivateFieldGet(this, _handler).emit('done', doneParameter);
  }

}

/**
 * Checks if the current node is a block node and empty.
 *
 * @param node - the prosemirror node
 *
 * @public
 */

function isEmptyBlockNode(node) {
  return bool(node) && node.type.isBlock && !node.textContent && !node.childCount;
}

/**
 * Checks the transaction for changes or compares the state with the previous
 * state.
 *
 * Return `true` when a change is detected in the document or the selection.
 */
function hasStateChanged(parameter) {
  var tr = parameter.tr,
      state = parameter.state,
      previousState = parameter.previousState;

  if (tr) {
    return hasTransactionChanged(tr);
  }

  return !state.doc.eq(previousState.doc) || !state.selection.eq(previousState.selection);
}

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var emptyRect = {
  top: -999999,
  left: -999999,
  right: -999999,
  bottom: -999999,
  height: 0,
  width: 0,
  x: -999999,
  y: -999999
};
var emptyVirtualPosition = {
  rect: /*#__PURE__*/_objectSpread$1( /*#__PURE__*/_objectSpread$1({}, emptyRect), {}, {
    toJSON: () => emptyRect
  })
};
/**
 * Render a floating selection positioner.
 *
 * - `rect` provides a viewport position which spans the width of the editor
 *   with a height identical to the cursor height.
 * - `top` - the top of the cursor.
 * - `bottom` - the bottom of the cursor.
 * - `left
 */

var floatingSelectionPositioner = /*#__PURE__*/Positioner.create({
  hasChanged: hasStateChanged,

  /**
   * This is only active for empty top level nodes. The data is the cursor start
   * and end position.
   */
  getActive(parameter) {
    var state = parameter.state,
        view = parameter.view;

    if (isSelectionEmpty(state) && state.selection.$anchor.depth === 1 && isEmptyBlockNode(state.selection.$anchor.parent)) {
      return [view.coordsAtPos(state.selection.$anchor.pos)];
    }

    return [];
  },

  getPosition(parameter) {
    var view = parameter.view,
        element = parameter.element,
        coords = parameter.data;
    var parent = element.offsetParent;

    if (!parent) {
      return emptyVirtualPosition;
    }

    var parentRect = parent.getBoundingClientRect();
    var editorRect = view.dom.getBoundingClientRect();
    var top = coords.top - parentRect.top;
    var bottom = coords.bottom - parentRect.top; // Since this is only active when the selection and node are empty, the left
    // position can be the cursor position.

    var left = coords.left - parentRect.left; // When used the right value will push the positioner to the extreme right
    // edge of the editor.

    var right = editorRect.right - parentRect.left;
    var rect = new DOMRect(coords.left, coords.top, right - left, coords.top - coords.bottom);
    return {
      top,
      bottom,
      left,
      right,
      rect
    };
  }

});
/**
 * Render a positioner which is centered around the selection. This is only
 * active for text selections, where the selection spans more than one
 * character.
 *
 * @remarks
 *
 * The menu will horizontally center itself `from` / `to` bounds of the current
 * selection.
 *
 * - `right` is undefined
 * - `left` will center your element based on the width of the current selection
 *   .
 * - `bottom` absolutely positions the element below the text selection.
 * - `top` absolutely positions the element above the text selection
 */

var centeredSelectionPositioner = /*#__PURE__*/Positioner.create({
  hasChanged: hasStateChanged,
  getActive: parameter => {
    var state = parameter.state,
        view = parameter.view;

    if (state.selection.empty) {
      return [];
    }

    var _state$selection = state.selection,
        from = _state$selection.from,
        to = _state$selection.to;
    var start = view.coordsAtPos(from);
    var end = view.coordsAtPos(to);
    return [{
      start,
      end
    }];
  },

  getPosition(parameter) {
    var element = parameter.element,
        data = parameter.data;
    var start = data.start,
        end = data.end;
    var parent = element.offsetParent;

    if (!parent) {
      return emptyVirtualPosition;
    } // The box in which the bubble menu is positioned, to use as an anchor


    var parentBox = parent.getBoundingClientRect(); // The bubble menu element

    var elementBox = element.getBoundingClientRect();
    var calculatedLeft = (start.left + end.left) / 2 - parentBox.left;
    var left = Math.min(parentBox.width - elementBox.width / 2, Math.max(calculatedLeft, elementBox.width / 2));
    var top = Math.trunc(start.top - parentBox.top);
    var bottom = Math.trunc(start.bottom - parentBox.top);
    var rect = new DOMRect(start.left, start.top, end.right - start.left, end.bottom - start.top);
    return {
      rect,
      left,
      top,
      bottom,
      right: left
    };
  }

});
/**
 * Render a menu that is inline with the first character of the selection. This
 * is useful for suggestions since they should typically appear while typing
 * without a multi character selection.
 *
 * @remarks
 *
 * The menu will center itself within the selection.
 *
 * - `right` should be used to absolutely position away from the right hand edge
 *   of the screen.
 * - `left` should be used to absolutely position away from the left hand edge
 *   of the screen.
 * - `bottom` absolutely positions the element above the text selection.
 * - `top` absolutely positions the element below the text selection
 */

var cursorPopupPositioner = /*#__PURE__*/Positioner.create({
  hasChanged: hasStateChanged,

  /**
   * Only active when the selection is empty (one character)
   */
  getActive: parameter => {
    var state = parameter.state,
        view = parameter.view;

    if (!state.selection.empty) {
      return [];
    }

    return [view.coordsAtPos(state.selection.from)];
  },

  getPosition(parameter) {
    var element = parameter.element,
        cursor = parameter.data;
    var parent = element.offsetParent;

    if (!parent) {
      return emptyVirtualPosition;
    } // The box in which the bubble menu is positioned, to use as an anchor


    var parentBox = parent.getBoundingClientRect(); // The popup menu element

    var elementBox = element.getBoundingClientRect();
    var calculatedLeft = cursor.left - parentBox.left;
    var calculatedRight = parentBox.right - cursor.right;
    var bottom = Math.trunc(cursor.bottom - parentBox.top);
    var top = Math.trunc(cursor.top - parentBox.top);
    var rect = new DOMRect(cursor.left, cursor.top, 0, cursor.bottom - cursor.top);
    var left = calculatedLeft + elementBox.width > parentBox.width ? calculatedLeft - elementBox.width : calculatedLeft;
    var right = calculatedRight + elementBox.width > parentBox.width ? calculatedRight - elementBox.width : calculatedRight;
    return {
      rect,
      right,
      left,
      bottom,
      top
    };
  }

});

var _dec, _class, _positioners, _temp;

function _createForOfIteratorHelper$1(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray$1(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = o[Symbol.iterator](); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray$1(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$1(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$1(o, minLen); }

function _arrayLikeToArray$1(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function ownKeys$2(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$2(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$2(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$2(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var PositionerExtension = (_dec = /*#__PURE__*/extensionDecorator({
  defaultOptions: {
    scrollDebounce: 50
  },
  customHandlerKeys: ['positioner'],
  staticKeys: ['scrollDebounce']
}), /*#__PURE__*/_dec(_class = (_temp = (_positioners = /*#__PURE__*/new WeakMap(), class PositionerExtension extends PlainExtension {
  constructor() {
    super(...arguments);

    _positioners.set(this, {
      writable: true,
      value: []
    });

    _defineProperty(this, "onAddCustomHandler", (_ref) => {
      var positioner = _ref.positioner;

      if (!positioner) {
        return;
      }

      _classPrivateFieldSet(this, _positioners, [..._classPrivateFieldGet(this, _positioners), positioner]);

      return () => {
        _classPrivateFieldSet(this, _positioners, _classPrivateFieldGet(this, _positioners).filter(handler => handler !== positioner));
      };
    });
  }

  get name() {
    return 'positioner';
  }

  init() {
    this.onScroll = debounce(this.options.scrollDebounce, this.onScroll.bind(this));
  }

  getParameter(_ref2) {
    var _this$store$previousS;

    var event = _ref2.event,
        firstUpdate = _ref2.firstUpdate;
    var state = this.store.getState();
    var previousState = (_this$store$previousS = this.store.previousState) !== null && _this$store$previousS !== void 0 ? _this$store$previousS : state;
    return {
      event,
      firstUpdate,
      previousState,
      state,
      scrollTop: this.store.view.dom.scrollTop
    };
  }

  onScroll() {
    var parameter = this.getParameter({
      event: 'scroll',
      firstUpdate: false
    });
    this.positioner(parameter);
  }

  createPlugin() {
    return {
      props: {
        handleDOMEvents: {
          scroll: () => {
            this.onScroll();
            return false;
          }
        }
      }
    };
  }

  onStateUpdate(update) {
    this.positioner(_objectSpread$2(_objectSpread$2({}, update), {}, {
      event: 'state',
      scrollTop: this.store.view.dom.scrollTop,
      firstUpdate: false
    }));
  }

  positioner(update) {
    var _iterator = _createForOfIteratorHelper$1(_classPrivateFieldGet(this, _positioners)),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var positioner = _step.value;
        var eventIsNotSupported = !positioner.events.includes(update.event);

        if (eventIsNotSupported) {
          continue;
        }

        this.triggerPositioner(positioner, update);
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  }

  triggerPositioner(positioner, update) {
    if (!positioner.hasChanged(update)) {
      // Nothing has changed so return without calling the change handler.
      return;
    }

    positioner.onActiveChanged(_objectSpread$2(_objectSpread$2({}, update), {}, {
      view: this.store.view
    }));
  }

}), _temp)) || _class);
var positioners = {
  bubble: centeredSelectionPositioner,
  centeredSelection: centeredSelectionPositioner,
  floating: floatingSelectionPositioner,
  floatingSelection: floatingSelectionPositioner,
  popup: cursorPopupPositioner,
  cursor: cursorPopupPositioner
};
function getPositioner(positioner) {
  if (isString(positioner)) {
    return positioners[positioner];
  }

  return positioner;
}

export { Positioner, PositionerExtension, centeredSelectionPositioner, cursorPopupPositioner, emptyVirtualPosition, floatingSelectionPositioner, getPositioner, hasStateChanged, isEmptyBlockNode };
